/*
 * Facturation d'une sous-section de la todolist (Accountable, e-mail ou domiciliation).
 * 1. Réglages : date d'émission ; domiciliation : date de prélèvement (aujourd'hui + 5 jours ouvrables TARGET2).
 * 2. Préparation : numéro figé, PDF généré, montant enregistré (un nouvel essai bloque si le montant change).
 * 3. Validation : aperçu des PDF et récapitulatif de tout ce qui va partir, puis confirmation explicite.
 * 4. Envoi : Accountable (toutes les factures) ; e-mail au membre si coché sur sa ligne (par défaut : nouveau membre,
 *    dont le mois d'entrée est le mois facturé ; domiciliation : toujours, pré-notification) ; domiciliation : fichier XML.
 *    Chaque ligne : modèle d'e-mail au choix, aperçu de l'e-mail réel, texte modifiable pour ce membre.
 * Ce qui est déjà parti n'est jamais renvoyé.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle, BookOpenCheck, CheckCircle2, Eye, FileDown, Landmark, Loader2, Mail, Receipt, RotateCcw, Send, XCircle,
} from 'lucide-react';
import type { SharingOperation } from './types';
import type { BillingChannel, TodoItem } from '../../../supabase/functions/_shared/crmTodo';
import { BILLING_CHANNEL_LABELS } from '../../../supabase/functions/_shared/crmTodo';
import {
  brusselsNow, collectionDateError, importDeadlineDate, maskIban, proposeCollectionDate,
} from '../../../supabase/functions/_shared/sepa';
import {
  ACCOUNTABLE_ADDRESSES, type AccountingSettings, type BuiltDocument, DEFAULT_ACCOUNTING_SETTINGS, blobToBase64, buildDocument,
  defaultMessageKind, fetchAccountingSettings, fetchEntryMonths, isFirstPeriod, itemKey, renderDocumentMessage, saveIssueDate, sendAccountingDocument,
} from './accounting';
import { type MemberEmailChoice, MemberEmailControls } from './MemberEmailControls';
import {
  type BillingInvoice, type BillingRow, billingInfoFor, createSepaBatch, downloadText, formatEuro, isStarted, monthCollectedTotal,
  reserveInvoice, updateInvoice,
} from './billing';
import { DEFAULT_SEPA_SETTINGS, type SepaSettings, fetchSepaSettings } from './sepaSettings';
import { setTodoDone } from './api';
import { ModalShell, errorMessage, formatDateFr, inputClass } from './ui';

type Phase = 'setup' | 'preparing' | 'review' | 'sending' | 'done';
type RowStatus = 'idle' | 'preparing' | 'ready' | 'blocked' | 'sending' | 'sent' | 'notified' | 'error';

interface RowState {
  status: RowStatus;
  message?: string;
  record?: BillingInvoice;
  doc?: BuiltDocument;
}

interface Props {
  channel: BillingChannel;
  rows: BillingRow[];
  operations: SharingOperation[];
  onClose: () => void;
  /** Factures créées ou modifiées (mise à jour de la todolist). */
  onRecords: (records: BillingInvoice[]) => void;
  /** Tâche facturée et cochée. */
  onDone: (item: TodoItem) => void;
}

const CHANNEL_ICONS = { peppol: BookOpenCheck, email: Mail, direct_debit: Landmark } as const;
const sameAmount = (a: number | null | undefined, b: number) => a !== null && a !== undefined && Math.abs(Number(a) - b) < 0.005;
const fr = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

export function BillingRunDialog({ channel, rows, operations, onClose, onRecords, onDone }: Props) {
  const label = BILLING_CHANNEL_LABELS[channel];
  const Icon = CHANNEL_ICONS[channel];
  const directDebit = channel === 'direct_debit';
  const today = brusselsNow().date;

  const [accounting, setAccounting] = useState<AccountingSettings | null>(null);
  const [sepa, setSepa] = useState<SepaSettings | null>(null);
  const [issueDate, setIssueDate] = useState(today);
  const [collectionDate, setCollectionDate] = useState('');
  const [phase, setPhase] = useState<Phase>('setup');
  /** Mois d'entrée de chaque membre (itemKey) : nouveau membre = mois d'entrée dans la période facturée. */
  const [entryMonths, setEntryMonths] = useState<Record<string, string | null> | null>(null);
  /** E-mail au membre, ligne par ligne (clé : row.key). */
  const [emails, setEmails] = useState<Record<string, MemberEmailChoice>>({});
  // État des lignes : lu par les traitements en cours (ref) et affiché (state).
  const statesRef = useRef<Record<string, RowState>>({});
  const [states, setStates] = useState<Record<string, RowState>>({});
  const [monthTotal, setMonthTotal] = useState<number | null>(null);
  const [xml, setXml] = useState<{ status: 'idle' | 'running' | 'done' | 'error'; message?: string; fileName?: string; count?: number; total?: number }>({ status: 'idle' });

  useEffect(() => {
    fetchAccountingSettings()
      .catch(err => { toast.error(`Réglages de comptabilité : ${errorMessage(err)}`); return DEFAULT_ACCOUNTING_SETTINGS; })
      .then(setAccounting);
    fetchSepaSettings()
      .catch(err => { toast.error(`Domiciliation : ${errorMessage(err)}`); return DEFAULT_SEPA_SETTINGS; })
      .then(s => {
        setSepa(s);
        // Factures déjà notifiées : leur date de prélèvement est figée.
        const frozenDate = rows.find(r => r.record?.member_sent_at && r.record.collection_date)?.record?.collection_date;
        setCollectionDate(frozenDate ?? proposeCollectionDate(today, s));
      });
    fetchEntryMonths(rows.map(r => r.item))
      .catch(err => { toast.error(`Dates d’entrée des membres : ${errorMessage(err)}`); return {} as Record<string, string | null>; })
      .then(months => {
        setEntryMonths(months);
        // Par défaut : e-mail aux nouveaux membres seulement (domiciliation : toujours, pré-notification obligatoire).
        setEmails(Object.fromEntries(rows.map(r => {
          const first = isFirstPeriod(r.item, months[itemKey(r.item)]);
          const statement = r.record?.amount !== null && r.record?.amount !== undefined && Number(r.record.amount) < 0;
          return [r.key, { send: directDebit || first, kind: defaultMessageKind({ statement, first, directDebit }), kindTouched: false, override: null }];
        })));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isFirst = (row: BillingRow) => isFirstPeriod(row.item, entryMonths?.[itemKey(row.item)]);
  const emailOf = (row: BillingRow): MemberEmailChoice => emails[row.key]
    ?? { send: directDebit, kind: defaultMessageKind({ statement: false, first: false, directDebit }), kindTouched: false, override: null };
  const setEmail = (key: string, p: Partial<MemberEmailChoice>) => setEmails(prev => ({ ...prev, [key]: { ...emailOfKey(prev, key), ...p } }));
  const emailOfKey = (all: Record<string, MemberEmailChoice>, key: string) =>
    all[key] ?? { send: directDebit, kind: defaultMessageKind({ statement: false, first: false, directDebit }), kindTouched: false, override: null };
  /** E-mail envoyé au membre : domiciliation toujours (pré-notification), sinon case cochée sur la ligne. */
  const wantsMail = (row: BillingRow) => directDebit || emailOf(row).send;

  const stateOf = (key: string): RowState => statesRef.current[key] ?? states[key] ?? { status: 'idle' };
  const patch = (key: string, p: Partial<RowState>) => {
    statesRef.current = { ...statesRef.current, [key]: { ...(statesRef.current[key] ?? { status: 'idle' }), ...p } };
    setStates(statesRef.current);
  };
  const opName = (id: string | null) => operations.find(o => o.id === id)?.name ?? '';

  /* Domiciliation : une seule date pour le PDF, l'e-mail et le XML. */
  const frozenDates = Array.from(new Set(rows.filter(r => r.record?.member_sent_at && r.record.collection_date).map(r => r.record!.collection_date!)));
  const dateLocked = directDebit && frozenDates.length > 0;
  // Date figée par des factures déjà notifiées : encore valable pour les membres à prévenir maintenant ?
  const needsNotice = rows.some(r => !r.record?.member_sent_at);
  const dateError = directDebit && sepa && collectionDate && (!dateLocked || needsNotice)
    ? collectionDateError(collectionDate, today, sepa)
    : null;
  const mixedDates = directDebit && frozenDates.length > 1;

  const setupBlocker = !accounting || !sepa || !entryMonths ? 'Chargement…'
    : !issueDate ? 'Indiquez la date d’émission'
      : directDebit && !collectionDate ? 'Indiquez la date de prélèvement'
        : dateError && dateLocked
          ? `Prélèvement déjà annoncé pour le ${fr(collectionDate)} : trop proche pour prévenir les autres membres. Traitez séparément les factures déjà notifiées (décochez les unes ou les autres)`
          : dateError ?? (mixedDates ? 'Factures déjà notifiées avec des dates de prélèvement différentes : traitez-les séparément' : null);

  /* ── 2. Préparation ── */
  const prepare = async () => {
    if (setupBlocker || !sepa) return;
    setPhase('preparing');
    const records: BillingInvoice[] = [];
    for (const row of rows) {
      const key = row.key;
      patch(key, { status: 'preparing', message: undefined });
      try {
        let record = await reserveInvoice(row, { issueDate, collectionDate: directDebit ? collectionDate : null });
        records.push(record);
        if (record.sepa_batch_id) { patch(key, { status: 'blocked', record, message: 'Déjà dans un fichier de domiciliation' }); continue; }
        const doc = await buildDocument(row.item, record.issue_date, billingInfoFor(record, sepa.creditorId));
        if (doc.invoiceNumber !== record.invoice_number) throw new Error(`Numéro imprimé ${doc.invoiceNumber} différent de ${record.invoice_number}`);
        const amount = doc.netAmount;
        if (isStarted(record) && !sameAmount(record.amount, amount)) {
          patch(key, { status: 'blocked', record, doc, message: `Montant recalculé ${formatEuro(amount)} différent de la facture émise (${formatEuro(Number(record.amount))}) : envoi bloqué` });
          continue;
        }
        if (!sameAmount(record.amount, amount) || !record.document_kind) {
          record = await updateInvoice(record.id, {
            amount,
            document_kind: amount < 0 ? 'statement' : 'invoice',
            accountable_target: amount < 0 ? 'expense' : 'revenue',
          });
          records.push(record);
        }
        if (directDebit && amount > sepa.maxAmount) {
          patch(key, { status: 'blocked', record, doc, message: `${formatEuro(amount)} dépasse le plafond de ${formatEuro(sepa.maxAmount)} par prélèvement` });
          continue;
        }
        if (directDebit && record.member_sent_at && amount > 0 && record.collection_date && record.collection_date <= today) {
          patch(key, { status: 'blocked', record, doc, message: `Membre prévenu pour un prélèvement le ${fr(record.collection_date)}, date dépassée : à régler à la main` });
          continue;
        }
        patch(key, { status: 'ready', record, doc });
      } catch (err) {
        patch(key, { status: 'error', message: errorMessage(err) });
      }
    }
    onRecords(records);
    // Montant connu : un montant négatif est un décompte (modèle adapté, sauf modèle choisi à la main).
    setEmails(prev => {
      const next = { ...prev };
      for (const row of rows) {
        const amount = statesRef.current[row.key]?.doc?.netAmount;
        const cur = emailOfKey(prev, row.key);
        if (amount === undefined || cur.kindTouched) continue;
        next[row.key] = { ...cur, kind: defaultMessageKind({ statement: amount < 0, first: isFirst(row), directDebit: directDebit && amount > 0 }) };
      }
      return next;
    });
    if (directDebit) {
      try { setMonthTotal(await monthCollectedTotal(collectionDate)); } catch { setMonthTotal(null); }
    }
    setPhase('review');
  };

  const ready = rows.filter(r => stateOf(r.key).status === 'ready');
  const amountOf = (r: BillingRow) => stateOf(r.key).doc?.netAmount ?? 0;
  const toDebit = directDebit ? ready.filter(r => amountOf(r) > 0) : [];
  const debitTotal = toDebit.reduce((acc, r) => acc + amountOf(r), 0);
  const capExceeded = directDebit && sepa && monthTotal !== null && monthTotal + debitTotal > sepa.maxMonthlyTotal + 0.004;
  const reviewBlocker = ready.length === 0 ? 'Aucune facture prête'
    : capExceeded ? `Plafond mensuel dépassé : ${formatEuro(monthTotal!)} déjà prélevés + ${formatEuro(debitTotal)} > ${formatEuro(sepa!.maxMonthlyTotal)}`
      : directDebit && monthTotal === null ? 'Total du mois indisponible : réessayez' : null;

  /* Ce qui va partir (validation avant envoi). */
  const plan = useMemo(() => {
    const p = { revenue: 0, expense: 0, members: 0, noMail: 0, skipped: [] as string[] };
    for (const r of ready) {
      const rec = stateOf(r.key).record!;
      const amount = amountOf(r);
      if (!rec.accountable_sent_at) { if (amount < 0) p.expense++; else p.revenue++; } else p.skipped.push(`${rec.invoice_number} : Accountable`);
      if (rec.member_sent_at) p.skipped.push(`${rec.invoice_number} : membre`);
      else if (wantsMail(r)) p.members++;
      else p.noMail++;
    }
    return p;
  }, [ready, states, emails]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = (row: BillingRow) => {
    const doc = stateOf(row.key).doc;
    if (!doc) return;
    const url = URL.createObjectURL(doc.blob);
    const w = window.open(url, '_blank', 'noopener');
    if (!w) { const a = document.createElement('a'); a.href = url; a.download = doc.fileName; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  /* ── 4. Envoi ── */
  /** Variables de l'e-mail d'une ligne : numéro et montant connus après la préparation. */
  const varsFor = (row: BillingRow): Record<string, string> => {
    const st = stateOf(row.key);
    const rec = st.record ?? row.record;
    const amount = st.doc?.netAmount;
    return {
      nom: row.item.title,
      periode: row.item.periodLabel,
      operation: opName(row.item.operationId),
      numero_facture: rec?.invoice_number ?? '[numéro attribué à la préparation]',
      montant: amount !== undefined
        ? Math.abs(amount).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '[montant calculé à la préparation]',
      date_prelevement: fr(rec?.collection_date ?? (directDebit ? collectionDate : null)),
      iban_masque: rec?.debtor_iban ? maskIban(rec.debtor_iban) : row.contact?.iban ? maskIban(row.contact.iban) : '',
      reference_mandat: rec?.mandate_reference ?? row.contact?.mandate_reference ?? '',
      identifiant_creancier: sepa?.creditorId ?? '',
    };
  };

  const messageFor = (row: BillingRow) => {
    const settings = accounting ?? DEFAULT_ACCOUNTING_SETTINGS;
    const choice = emailOf(row);
    return renderDocumentMessage(choice.override ?? settings.messages[choice.kind], varsFor(row));
  };

  const finish = async (row: BillingRow, rec: BillingInvoice, doc: BuiltDocument) => {
    const item = row.item;
    try {
      if (item.operationId) await saveIssueDate(item.operationId, doc, { startMonth: item.periodStart, endMonth: item.periodEnd }, rec.issue_date);
    } catch (err) {
      console.warn('Date d’émission non enregistrée :', errorMessage(err));
    }
    await setTodoDone({ kind: item.kind, ref: item.ref, period: item.period }, true);
    onDone(item);
  };

  const send = async () => {
    if (reviewBlocker) return;
    setPhase('sending');
    const updated: BillingInvoice[] = [];
    for (const row of ready) {
      const key = row.key;
      const st = stateOf(key);
      let rec = st.record!;
      const doc = st.doc!;
      const amount = doc.netAmount;
      const needAccountable = !rec.accountable_sent_at;
      const needMember = wantsMail(row) && !rec.member_sent_at;
      patch(key, { status: 'sending', message: undefined });
      try {
        if (needAccountable || needMember) {
          if (needMember && !rec.member_email) throw new Error('Pas d’adresse e-mail');
          const { subject, body } = messageFor(row);
          const res = await sendAccountingDocument({
            target: amount < 0 ? 'expense' : 'revenue',
            fileName: doc.fileName,
            pdfBase64: await blobToBase64(doc.blob),
            accountable: needAccountable,
            member: needMember ? { email: rec.member_email!, subject, message: body } : null,
          });
          const now = new Date().toISOString();
          const errors = [
            needAccountable && !res.accountable.success ? `Accountable : ${res.accountable.error ?? 'échec'}` : null,
            needMember && !res.member.success ? `E-mail : ${res.member.error ?? 'échec'}` : null,
          ].filter(Boolean) as string[];
          rec = await updateInvoice(rec.id, {
            ...(needAccountable && res.accountable.success ? { accountable_sent_at: now } : {}),
            ...(needMember && res.member.success ? { member_sent_at: now } : {}),
            last_error: errors.length ? errors.join(' · ') : null,
          });
          updated.push(rec);
          patch(key, { record: rec });
          // Domiciliation : sans e-mail de pré-notification, pas de prélèvement.
          if (errors.length) { patch(key, { status: 'error', message: errors.join(' · ') }); continue; }
        }
        if (directDebit && amount > 0) {
          patch(key, { status: 'notified', message: `Prévenu : prélèvement le ${fr(rec.collection_date)}` });
          continue;
        }
        rec = await updateInvoice(rec.id, { status: 'sent', status_date: today });
        updated.push(rec);
        await finish(row, rec, doc);
        patch(key, {
          status: 'sent', record: rec,
          message: directDebit ? 'Envoyée, pas de prélèvement (montant nul ou négatif)' : 'Envoyée',
        });
      } catch (err) {
        patch(key, { status: 'error', message: errorMessage(err) });
      }
    }
    onRecords(updated);
    if (directDebit) await runXml(); else setPhase('done');
  };

  /** Fichier XML des membres prévenus (montant positif, pas encore dans un fichier). */
  const runXml = async () => {
    const candidates = rows.filter(r => {
      const st = stateOf(r.key);
      const rec = st.record;
      return rec && rec.member_sent_at && !rec.sepa_batch_id && (st.doc?.netAmount ?? 0) > 0 && (st.status === 'notified' || st.status === 'ready');
    });
    const ids = candidates.map(r => stateOf(r.key).record!.id);
    if (ids.length === 0) { setXml({ status: 'idle' }); setPhase('done'); return; }
    setXml({ status: 'running' });
    try {
      const res = await createSepaBatch(ids);
      downloadText(res.xml, res.file_name);
      const inBatch: BillingInvoice[] = [];
      for (const row of candidates) {
        const st = stateOf(row.key);
        const rec: BillingInvoice = { ...st.record!, sepa_batch_id: res.batch.id, status: 'to_collect', status_date: res.batch.collection_date };
        inBatch.push(rec);
        await finish(row, rec, st.doc!);
        patch(row.key, { status: 'sent', record: rec, message: `Dans le fichier ${res.file_name}` });
      }
      onRecords(inBatch);
      res.warnings?.forEach(w => toast(w, { duration: 10000 }));
      setXml({ status: 'done', fileName: res.file_name, count: res.batch.tx_count, total: Number(res.batch.total) });
    } catch (err) {
      setXml({ status: 'error', message: errorMessage(err) });
    }
    setPhase('done');
  };


  const close = () => {
    if (phase === 'preparing' || phase === 'sending' || xml.status === 'running') { toast('Traitement en cours : patientez jusqu’à la fin'); return; }
    onClose();
  };

  /* ── Affichage ── */
  const sentRows = rows.filter(r => stateOf(r.key).status === 'sent');
  const sentInvoices = sentRows.filter(r => amountOf(r) >= 0);
  const sentStatements = sentRows.filter(r => amountOf(r) < 0);
  const sentTotal = sentInvoices.reduce((acc, r) => acc + amountOf(r), 0);
  const statementTotal = sentStatements.reduce((acc, r) => acc - amountOf(r), 0);
  const failed = rows.filter(r => ['error', 'blocked'].includes(stateOf(r.key).status));
  const notifiedWithoutXml = rows.filter(r => stateOf(r.key).status === 'notified');
  const deadline = directDebit && collectionDate ? importDeadlineDate(collectionDate) : null;

  const statusCell = (row: BillingRow) => {
    const s = stateOf(row.key);
    const spin = (t: string) => <span className="flex items-center gap-1 text-neutral-500"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t}</span>;
    if (s.status === 'preparing') return spin('Préparation du PDF…');
    if (s.status === 'sending') return spin('Envoi…');
    if (s.status === 'sent') return <span className="flex items-start gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />{s.message}</span>;
    if (s.status === 'notified') return <span className="flex items-start gap-1 text-amber-700"><Landmark className="w-3.5 h-3.5 mt-px shrink-0" />{s.message}</span>;
    if (s.status === 'error' || s.status === 'blocked') return <span className="flex items-start gap-1 text-red-700"><XCircle className="w-3.5 h-3.5 mt-px shrink-0" />{s.message}</span>;
    return null;
  };

  const footer = (
    <>
      <span className="mr-auto self-center text-[11px] text-neutral-500">
        {phase === 'setup' && (setupBlocker ?? `${rows.length} facture${rows.length > 1 ? 's' : ''} à préparer`)}
        {phase === 'review' && (reviewBlocker ?? 'Vérifiez le récapitulatif avant de confirmer')}
        {phase === 'preparing' && `Préparation ${rows.filter(r => !['idle', 'preparing'].includes(stateOf(r.key).status)).length} / ${rows.length}…`}
        {phase === 'sending' && `Envoi ${rows.filter(r => ['sent', 'notified', 'error'].includes(stateOf(r.key).status)).length} / ${ready.length}…`}
      </span>
      <button onClick={close} className="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:bg-white">
        {phase === 'done' ? 'Fermer' : 'Annuler'}
      </button>
      {phase === 'setup' && (
        <button onClick={prepare} disabled={!!setupBlocker} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700 disabled:opacity-40">
          <Receipt className="w-4 h-4" />Préparer les factures ({rows.length})
        </button>
      )}
      {phase === 'review' && (
        <button onClick={send} disabled={!!reviewBlocker} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-sm font-semibold text-neutral-900 disabled:opacity-40">
          <Send className="w-4 h-4" />Confirmer et envoyer ({ready.length})
        </button>
      )}
      {phase === 'done' && xml.status === 'error' && notifiedWithoutXml.length > 0 && (
        <button onClick={runXml} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700">
          <RotateCcw className="w-4 h-4" />Recréer le fichier XML
        </button>
      )}
    </>
  );

  return (
    <ModalShell
      title={`Facturer : ${label}`}
      subtitle={`${rows.length} facture${rows.length > 1 ? 's' : ''} · ${Array.from(new Set(rows.map(r => r.item.periodLabel))).join(', ')}`}
      onClose={close}
      width="max-w-4xl"
      zIndex="z-[80]"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Réglages */}
        <div className="grid gap-3 md:grid-cols-2">
          <label className="block">
            <span className="block text-[11px] font-medium text-neutral-600 mb-1">Date d’émission</span>
            <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} disabled={phase !== 'setup'} className={`${inputClass} border-neutral-200`} />
            <span className="block text-[10px] text-neutral-400 mt-1">Imprimée sur la facture. Une facture déjà envoyée garde sa date.</span>
          </label>
          {directDebit && (
            <label className="block">
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Date de prélèvement</span>
              <input type="date" value={collectionDate} onChange={e => setCollectionDate(e.target.value)} disabled={phase !== 'setup' || dateLocked}
                className={`${inputClass} ${dateError ? 'border-red-300' : 'border-neutral-200'}`} />
              <span className={`block text-[10px] mt-1 ${dateError ? 'text-red-600' : 'text-neutral-400'}`}>
                {dateLocked
                  ? 'Date des factures déjà notifiées : elle ne change plus.'
                  : dateError ?? `Proposée : aujourd’hui + ${sepa?.minBusinessDays ?? 5} jours ouvrables (TARGET2). Même date sur le PDF, l’e-mail et le fichier XML.`}
              </span>
            </label>
          )}
        </div>

        {/* E-mails aux membres : choix par ligne */}
        {(phase === 'setup' || phase === 'review') && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-neutral-200 bg-neutral-50/60 px-3 py-2 text-[11px] text-neutral-600">
            <span>
              <Mail className="inline w-3.5 h-3.5 mr-1 -mt-px" />
              E-mail au membre : <b>{rows.filter(wantsMail).length}</b> / {rows.length}
              {directDebit ? ' (domiciliation : toujours, pré-notification du prélèvement)' : ' · par défaut, les nouveaux membres (mois d’entrée = mois facturé)'}
            </span>
            {!directDebit && (
              <span className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => setEmails(prev => Object.fromEntries(rows.map(r => [r.key, { ...emailOfKey(prev, r.key), send: true }])))}
                  className="text-neutral-500 hover:text-neutral-900 underline-offset-2 hover:underline">Tout cocher</button>
                <button type="button" onClick={() => setEmails(prev => Object.fromEntries(rows.map(r => [r.key, { ...emailOfKey(prev, r.key), send: isFirst(r) }])))}
                  className="text-neutral-500 hover:text-neutral-900 underline-offset-2 hover:underline">Nouveaux membres</button>
                <button type="button" onClick={() => setEmails(prev => Object.fromEntries(rows.map(r => [r.key, { ...emailOfKey(prev, r.key), send: false }])))}
                  className="text-neutral-500 hover:text-neutral-900 underline-offset-2 hover:underline">Aucun</button>
              </span>
            )}
          </div>
        )}

        {/* Validation : ce qui va être fait */}
        {phase === 'review' && (
          <div className={`rounded-xl border p-4 space-y-2 ${reviewBlocker ? 'border-red-200 bg-red-50/60' : 'border-[#F8BD3D] bg-amber-50/60'}`}>
            <p className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900"><Icon className="w-4 h-4" />Ce qui va être fait après confirmation</p>
            <ul className="text-xs text-neutral-700 space-y-1 list-disc pl-5">
              {plan.revenue > 0 && <li>{plan.revenue} facture{plan.revenue > 1 ? 's' : ''} envoyée{plan.revenue > 1 ? 's' : ''} à <b>{ACCOUNTABLE_ADDRESSES.revenue}</b></li>}
              {plan.expense > 0 && <li>{plan.expense} décompte{plan.expense > 1 ? 's' : ''} (montant négatif) envoyé{plan.expense > 1 ? 's' : ''} à <b>{ACCOUNTABLE_ADDRESSES.expense}</b></li>}
              {plan.members > 0 && (
                <li>
                  {plan.members} e-mail{plan.members > 1 ? 's' : ''} aux membres avec le PDF, selon le modèle choisi sur chaque ligne
                  {directDebit ? ` (pré-notification : prélèvement le ${fr(collectionDate)})` : ''}
                </li>
              )}
              {plan.noMail > 0 && <li>{plan.noMail} membre{plan.noMail > 1 ? 's' : ''} sans e-mail (case non cochée) : facture disponible dans l’espace membre</li>}
              {directDebit && toDebit.length > 0 && (
                <li>
                  1 fichier XML ({sepa?.format}) : {toDebit.length} prélèvement{toDebit.length > 1 ? 's' : ''}, total <b>{formatEuro(debitTotal)}</b>, le {fr(collectionDate)},
                  téléchargé et archivé. Import dans Easy Banking Business au plus tard le {fr(deadline)} à {sepa?.cutoffHour ?? 13} h.
                </li>
              )}
              {directDebit && ready.length > toDebit.length && <li>{ready.length - toDebit.length} facture{ready.length - toDebit.length > 1 ? 's' : ''} sans prélèvement (montant nul ou négatif)</li>}
              {directDebit && monthTotal !== null && sepa && (
                <li>Total des prélèvements de {collectionDate.slice(5, 7)}/{collectionDate.slice(0, 4)} : {formatEuro(monthTotal + debitTotal)} sur {formatEuro(sepa.maxMonthlyTotal)} autorisés</li>
              )}
              {plan.skipped.length > 0 && <li className="text-neutral-500">Déjà parti, non renvoyé : {plan.skipped.join(', ')}</li>}
              <li>Tâches cochées dans la todolist</li>
            </ul>
            {reviewBlocker && <p className="flex items-start gap-1.5 text-xs text-red-700"><AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />{reviewBlocker}</p>}
          </div>
        )}

        {/* Récapitulatif final */}
        {phase === 'done' && (
          <div className="rounded-xl border border-neutral-200 p-4 space-y-1.5">
            <p className="text-sm font-semibold text-neutral-900">Récapitulatif</p>
            <p className="text-xs text-neutral-700">
              {sentInvoices.length} facture{sentInvoices.length > 1 ? 's' : ''} traitée{sentInvoices.length > 1 ? 's' : ''}, total {formatEuro(sentTotal)}
              {sentStatements.length > 0 && <> · {sentStatements.length} décompte{sentStatements.length > 1 ? 's' : ''}, total {formatEuro(statementTotal)}</>}
              {failed.length > 0 && <span className="text-red-700"> · {failed.length} en erreur</span>}
            </p>
            {xml.status === 'done' && (
              <p className="flex items-center gap-1.5 text-xs text-emerald-700">
                <FileDown className="w-3.5 h-3.5" />Fichier {xml.fileName} : {xml.count} prélèvement{(xml.count ?? 0) > 1 ? 's' : ''}, {formatEuro(xml.total ?? 0)}, téléchargé et archivé.
                Import avant le {fr(deadline)} à {sepa?.cutoffHour ?? 13} h.
              </p>
            )}
            {xml.status === 'error' && <p className="text-xs text-red-700">Fichier XML non créé : {xml.message}. Les membres prévenus restent à inclure : « Recréer le fichier XML ».</p>}
            {failed.length > 0 && (
              <ul className="text-[11px] text-red-700 list-disc pl-5">
                {failed.map(r => <li key={r.key}>{r.item.title} : {stateOf(r.key).message}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* Factures */}
        <ul className="border border-neutral-200 rounded-xl divide-y divide-neutral-100">
          {rows.map(row => {
            const s = stateOf(row.key);
            const rec = s.record ?? row.record;
            return (
              <li key={row.key} className="flex items-start gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-neutral-900">
                    <span className="font-medium">{row.item.title}</span> <span className="text-neutral-500">· {row.item.periodLabel}</span>
                    {rec && <span className="ml-1.5 font-mono text-[11px] text-neutral-500">{rec.invoice_number}</span>}
                  </p>
                  <p className="text-[11px] text-neutral-500 truncate">
                    {s.doc ? <b className="text-neutral-800">{formatEuro(s.doc.netAmount)}</b> : rec?.amount !== null && rec?.amount !== undefined ? formatEuro(Number(rec.amount)) : '—'}
                    {' · '}{row.email ?? <span className="text-amber-700">pas d’e-mail</span>}
                    {directDebit && row.contact && <> · {maskIban(row.contact.iban)} · mandat {row.contact.mandate_reference}</>}
                    {rec?.accountable_sent_at && <> · Accountable le {formatDateFr(rec.accountable_sent_at)}</>}
                    {rec?.member_sent_at && <> · membre le {formatDateFr(rec.member_sent_at)}</>}
                  </p>
                  {accounting && (
                    <MemberEmailControls
                      title={row.item.title}
                      email={row.email}
                      choice={emailOf(row)}
                      onChange={p => setEmail(row.key, p)}
                      templates={accounting.messages}
                      vars={varsFor(row)}
                      sendLockedReason={directDebit ? 'Domiciliation : l’e-mail de pré-notification est obligatoire avant le prélèvement' : null}
                      sentAt={rec?.member_sent_at ?? null}
                      disabled={phase !== 'setup' && phase !== 'review'}
                      first={isFirst(row)}
                    />
                  )}
                  <div className="text-[11px] mt-0.5">{statusCell(row)}</div>
                </div>
                {s.doc && (
                  <button onClick={() => preview(row)} title="Ouvrir le PDF" className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 whitespace-nowrap">
                    <Eye className="w-3.5 h-3.5" />Aperçu
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        <p className="text-[10px] text-neutral-400">
          Le PDF est celui de l’outil de factures (une facture par société dans un groupe). Préparation : quelques secondes par facture, gardez la fenêtre ouverte.
          {directDebit && ` Contrôles : IBAN, mandat actif, montant ≤ ${formatEuro(sepa?.maxAmount ?? 2500)}, total du mois ≤ ${formatEuro(sepa?.maxMonthlyTotal ?? 10000)}.`}
          {directDebit && ` Le membre dont l’e-mail échoue n’est pas prélevé. Fichier à importer au plus tard la veille ouvrée du prélèvement, avant ${sepa?.cutoffHour ?? 13} h.`}
        </p>
      </div>
    </ModalShell>
  );
}
